import { Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    DisruptionMessage,
    DisruptionMessageEntity,
    MessageLines,
    MessageTypeService,
    AlertUtil,
    AlertUrls
} from '@ridango/core';
import { ApplicationFacade, ShapeType } from '@ridango/components';
import uniqBy from 'lodash/uniqBy';
import Env from '@ridango/core/env';
import { Temporal } from '@js-temporal/polyfill';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

interface DisruptionMessageViewModel extends DisruptionMessage {
    isGeneric: boolean;
    effectType: string | null;
    isBus: boolean;
    isStop: boolean;
    isBusAndStop: boolean;
    lines: MessageLines[];
    stops: string[];
    header: string;
    description: string;
    activePeriod: [Temporal.Instant, Temporal.Instant];
    processedAlertUrls: AlertUrls[];
}

@Component({
    selector: 'rwidget-disruption-messages-accordion',
    templateUrl: './disruption-messages-accordion.component.html',
    styleUrls: ['./disruption-messages-accordion.component.scss']
})
export class DisruptionMessagesAccordionComponent implements OnChanges {
    @Input() messages: DisruptionMessage[];
    public viewMessages$: Observable<DisruptionMessageViewModel[]>;
    private messages$ = new ReplaySubject<DisruptionMessage[]>(1);

    public selectedImageConfig: AlertUrls;
    ShapeType = ShapeType;
    assetsPath$ = this.applicationFacade.assetsPath$;
    public dateRangeFormatOptions = this.env.FORMATTING?.DISRUPTION_MESSAGE_DATE_RANGE;
    private readonly ALERT = 'Alert';

    constructor(
        private translateService: TranslateService,
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private messageTypeService: MessageTypeService,
        private analyticsService: AnalyticsService
    ) {
        this.viewMessages$ = this.messages$.pipe(
            map((messages) =>
                (messages || []).map((message) => ({
                    ...message,
                    isGeneric: this.messageTypeService.isGenericDisruption(message),
                    effectType: this.messageTypeService.isEffectDisruption(message),
                    isBus: this.messageTypeService.isBusDisruption(message),
                    isStop: this.messageTypeService.isStopDisruption(message),
                    isBusAndStop: this.messageTypeService.isBusAndStopDisruption(message),
                    lines: this.getMessageLines(message.informedEntities),
                    stops: this.getMessageStops(message),
                    header: this.getMessageHeaderText(message),
                    description: this.getMessageDescriptionText(message),
                    activePeriod: this.activePeriodRange(message),
                    processedAlertUrls: (message.alertUrls || []).map((url) => AlertUtil.processAlertUrl(url))
                }))
            )
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.messages) {
            this.messages$.next(changes.messages.currentValue);
        }
    }

    getMessageLines(informedEntities: DisruptionMessageEntity[]): MessageLines[] {
        if (!informedEntities || informedEntities.length === 0) {
            return [];
        }

        return uniqBy(
            informedEntities
                .filter((entity) => entity?.type === 'ROUTE' && entity?.routeShortName?.trim())
                .map((entity) => ({
                    line: entity.routeShortName.trim(),
                    icon: entity?.icon
                })),
            'line'
        );
    }

    getMessageStops(message: DisruptionMessage): string[] {
        return message.informedEntities
            .map((entity) => entity.stopName?.trim())
            .filter((value) => !!value)
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    private getMessageHeaderText(message: DisruptionMessage): string {
        const headerTextArray = Array.isArray(message?.headerText) ? message.headerText : [];
        const translation = headerTextArray.find(
            (translation) => translation.language === this.translateService.currentLang
        );
        return translation ? translation.text : message.headerTextDefault;
    }

    private getMessageDescriptionText(message: DisruptionMessage): string {
        const descriptionTextArray = Array.isArray(message?.descriptionText) ? message.descriptionText : [];
        const translation = descriptionTextArray.find(
            (translation) => translation.language === this.translateService.currentLang
        );
        return translation ? translation.text : message.descriptionTextDefault;
    }

    private activePeriodRange(message: DisruptionMessage): [Temporal.Instant, Temporal.Instant] {
        const { activePeriodStart, activePeriodEnd } = message;
        const toInstant = (date: string | Date) =>
            date instanceof Date ? Temporal.Instant.fromEpochMilliseconds(date.getTime()) : Temporal.Instant.from(date);
        return [toInstant(activePeriodStart), toInstant(activePeriodEnd)];
    }

    openImageFullScreen(url: AlertUrls) {
        this.selectedImageConfig = url;
    }

    closeSelectedImageModal() {
        this.selectedImageConfig = null;
    }

    onAccordionClick() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: this.ALERT
        });
    }
}
